import {
  Cancel01Icon,
  Delete03Icon,
  StatusIcon,
  UserCircle02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RowSelectionState } from "@tanstack/react-table";
import { instance } from "api/apiBase";
import { leadApi } from "api/lead";
import { toast } from "react-toastify";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  Modal,
  IconButtonVariant
} from "shared/components";
import { useState } from "react";
import { AssigneeSelect } from "./AssigneeSelect";

export const SelectPopUp = ({
  selectedRow,
  clearSelectedRows
}: {
  selectedRow: RowSelectionState;
  clearSelectedRows: () => void;
}) => {
  const queryClient = useQueryClient();
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState<number | null>(
    null
  );

  const { mutateAsync: deleteLeads, isPending: isDeleting } = useMutation({
    mutationKey: ["BULK", "DELETE", "LEADS"],
    onSuccess: (_, variables) => {
      clearSelectedRows();
      queryClient.refetchQueries({ queryKey: ["LEADS_TABLE"] });
      toast.success(
        `${variables.length} ${variables.length > 1 ? "leads" : "lead"} deleted successfully`
      );
    },
    mutationFn: async (lead_ids: number[]) => {
      return instance.delete(`/portal/v1/leads/bulk_delete`, {
        data: {
          lead_ids
        }
      });
    }
  });

  const { mutateAsync: bulkAssignLeads, isPending: isAssigning } = useMutation({
    mutationKey: ["BULK", "ASSIGN", "LEADS"],
    onSuccess: () => {
      clearSelectedRows();
      setIsAssignModalOpen(false);
      setSelectedAssigneeId(null);
      queryClient.refetchQueries({ queryKey: ["LEADS_TABLE"] });
      queryClient.refetchQueries({ queryKey: ["LEAD_ACTIVITY_LOG"] });
      toast.success("Leads assigned successfully");
    },
    onError: () => {
      toast.error("Failed to assign leads");
    },
    mutationFn: async ({
      lead_ids,
      assignee_ids
    }: {
      lead_ids: number[];
      assignee_ids: number[];
    }) => {
      return leadApi.bulkAssignLeads({
        lead_ids,
        assignee_ids
      });
    }
  });

  const handleBulkAssign = () => {
    const leadIds = Object.keys(selectedRow)
      .filter((key) => selectedRow[key])
      .map(Number);
    if (leadIds.length === 0) return;
    bulkAssignLeads({
      lead_ids: leadIds,
      assignee_ids: selectedAssigneeId !== null ? [selectedAssigneeId] : []
    });
  };

  const amountOfSelectedLeads =
    Object.values(selectedRow).filter(Boolean).length;

  return Object.values(selectedRow).filter(Boolean).length ? (
    <>
      <div className="h-16 divide-x-[1px] shadow-lg bg-white px-4 py-2 rounded-lg border border-alpha-100 z-[11] fixed right-10 bottom-24 animate-appear flex justify-end items-center gap-4">
        <p className="text-base font-medium text-content-1000 truncate">
          {amountOfSelectedLeads} {amountOfSelectedLeads > 1 ? "leads" : "lead"}{" "}
          selected
        </p>

        <Button
          size={ButtonSize.SM}
          HugeIcon={UserCircle02Icon}
          variant={ButtonVariant.TEXT}
          onClick={() => setIsAssignModalOpen(true)}
          className="flex items-center justify-center gap-2 pl-4"
        >
          Assign
        </Button>
        <Button
          size={ButtonSize.SM}
          HugeIcon={StatusIcon}
          variant={ButtonVariant.TEXT}
          onClick={() => alert("wip")}
          className="flex items-center justify-center gap-2 pl-4"
        >
          Change stage
        </Button>
        <Button
          size={ButtonSize.SM}
          HugeIcon={Delete03Icon}
          loading={isDeleting}
          variant={ButtonVariant.TEXT}
          className="flex items-center justify-center gap-2 pl-4"
          onClick={() => deleteLeads(Object.keys(selectedRow).map(Number))}
        >
          Delete
        </Button>

        <div className="pl-4 h-5 flex flex-shrink-0 items-center">
          <IconButton
            size={IconButtonSize.SM}
            variant={IconButtonVariant.TEXT_BASE}
            HugeIcon={Cancel01Icon}
            onClick={clearSelectedRows}
          />
        </div>
      </div>

      <Modal
        opened={isAssignModalOpen}
        onClose={() => {
          setIsAssignModalOpen(false);
          setSelectedAssigneeId(null);
        }}
        panelClassName="w-full max-w-[400px] p-6"
      >
        <div className="flex flex-col gap-4">
          <h3 className="text-xl leading-6 font-bold text-content-1000">
            Assign leads
          </h3>
          <div className="flex flex-col gap-2">
            <AssigneeSelect
              value={selectedAssigneeId}
              onChange={(partner_user_id) => {
                setSelectedAssigneeId(partner_user_id);
              }}
              label="Assign to"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              size={ButtonSize.SM}
              variant={ButtonVariant.BASE}
              onClick={() => {
                setIsAssignModalOpen(false);
                setSelectedAssigneeId(null);
              }}
            >
              Cancel
            </Button>
            <Button
              size={ButtonSize.SM}
              variant={ButtonVariant.PRIMARY}
              loading={isAssigning}
              onClick={handleBulkAssign}
            >
              Assign
            </Button>
          </div>
        </div>
      </Modal>
    </>
  ) : null;
};
